import { GetProps } from 'antd';
import isEmpty from 'lodash/isEmpty';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { StepProps } from '@/widgets/ServiceFormTemplate';
import { FormBlock } from '@/widgets/ServiceFormTemplate/ui/FormBlock';

import { ConditionView, FormRadioGroup, Grid, StepControlPanel } from '@/shared/ui';

import { Loader } from '@/shared/ui';

import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/api/queryKeys';

import { InsuranceProductsList } from '@/modules/bia/components/InsuranceProductsList';

import AuthContext from '@/contexts/Auth/AuthContext';

import { DocumentData } from '@/api/types';

import { FormDataType, UserRequestDraft } from '@/api/types';
import api from '@/api';

import { InsuranceSelectionSchema, InsuranceServiceError } from '../../model/types';
import { InsuranceServiceErrorInformer } from '../InsuranceServiceErrorInformer';

import { NoAccessWarningModal } from '../../NoAccessWarningModal';

export interface InsuranceSelectionProps {
  userRequestDraft: UserRequestDraft;
  onDraftCreate?: (
    data?: { stateForm: FormDataType },
    specificStep?: (step: number) => void,
  ) => Promise<Nullable<string>>;
}

export const InsuranceSelection: React.FC<StepProps<InsuranceSelectionProps>> = ({
  userRequestDraft,
  currentRequestId,
  onDraftCreate,
  specificStep,
  next,
}) => {
  const requestId = currentRequestId ?? userRequestDraft?.id;

  const { documentsData } = useContext(AuthContext);

  const passportDocument = useMemo<Nullable<DocumentData>>(() => documentsData['passport'], [documentsData]);

  const navigate = useNavigate();

  const hasTriedCreate = useRef(false);

  const { control, watch, setValue, handleSubmit, getValues } = useFormContext<InsuranceSelectionSchema>();

  const insuranceProduct = watch('insuranceProduct');

  const [insuranceServiceError, setInsuranceServiceError] = useState<Nullable<InsuranceServiceError>>(null);

  const [isNoAccessModalShown, setIsNoAccessModalShown] = useState(false);

  useEffect(() => {
    if (!isNoAccessModalShown && isEmpty(passportDocument)) {
      setIsNoAccessModalShown(true);
    }
  }, [passportDocument, isNoAccessModalShown]);

  const {
    data: insuranceProductsData,
    isLoading: areInsuranceProductsLoading,
    isFetched: areInsuranceProductsDataHasBeenFetched,
    refetch: refetchInsuranceProducts,
  } = useQuery({
    queryKey: queryKeys.bia.products(requestId ?? ''),
    queryFn: () => api.bia.products(requestId!),
    enabled: !!requestId && !isEmpty(passportDocument),
    retry: false,
  });

  useEffect(() => {
    if (!requestId && onDraftCreate && !hasTriedCreate.current) {
      hasTriedCreate.current = true;
      void onDraftCreate({ stateForm: getValues() as FormDataType }, specificStep);
    }
  }, [requestId, onDraftCreate]);

  useEffect(() => {
    if (areInsuranceProductsDataHasBeenFetched && !insuranceProductsData?.data?.length) {
      setInsuranceServiceError(InsuranceServiceError.INSURANCE_PRODUCTS_NO_DATA);
    }

    if (!areInsuranceProductsLoading && insuranceProductsData?.data?.length) {
      setValue('insuranceProduct', insuranceProductsData.data[0]);
    }
  }, [insuranceProductsData, areInsuranceProductsDataHasBeenFetched]);

  const handleLoadInsuranceProducts = async (): Promise<void> => {
    refetchInsuranceProducts().then(({ error }: any) => {
      if (error?.status === 500) {
        setInsuranceServiceError(InsuranceServiceError.AUTH);
      }
    });
  };

  const handleNextStep = handleSubmit(async () => {
    if (!currentRequestId && onDraftCreate) {
      try {
        await onDraftCreate({
          stateForm: getValues() as FormDataType,
        });
      } catch (error) {
        console.error(error);
        return;
      }
    }
    next();
  });

  const radioOptions = useMemo<GetProps<typeof FormRadioGroup>['options']>(
    () =>
      insuranceProductsData?.data.map((product) => ({
        value: product,
        checked: product.id === insuranceProduct?.id,
        label: <InsuranceProductsList insuranceProducts={[product]} />,
      })) ?? [],
    [insuranceProduct?.id, insuranceProductsData?.data],
  );

  if (isEmpty(passportDocument)) {
    return <NoAccessWarningModal isModalOpen={isNoAccessModalShown} navigate={navigate} />;
  }

  return (
    <ConditionView
      if={!!requestId && !areInsuranceProductsLoading && areInsuranceProductsDataHasBeenFetched}
      else={<Loader centered />}
    >
      <ConditionView
        if={!insuranceServiceError}
        else={
          <>
            <FormBlock title="Вибір страхового полісу">
              <InsuranceServiceErrorInformer error={insuranceServiceError} />
            </FormBlock>

            <ConditionView
              if={
                insuranceServiceError === InsuranceServiceError.INSURANCE_PRODUCTS_NO_DATA ||
                insuranceServiceError === InsuranceServiceError.AUTH
              }
            >
              <StepControlPanel
                nextBtnText="Оновити"
                disabledNext={areInsuranceProductsLoading}
                withArrow={false}
                next={handleLoadInsuranceProducts}
              />
            </ConditionView>
          </>
        }
      >
        <FormBlock title="Вибір страхового полісу">
          <FormRadioGroup name="insuranceProduct" control={control} options={radioOptions} />
        </FormBlock>

        <StepControlPanel next={handleNextStep} disabledNext={!insuranceProduct} />
      </ConditionView>
    </ConditionView>
  );
};

export const InsuranceSelectionPreview: React.FC = () => {
  const { getValues } = useFormContext<InsuranceSelectionSchema>();
  const { insuranceProduct } = getValues();

  return (
    <Grid
      title="Вибір страхового полісу"
      data={[
        ['Компанія', insuranceProduct?.organization.name],
        ['Загальна сума, грн', `${insuranceProduct?.price ?? ''}`],
      ]}
    />
  );
};
